import { Component, Inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { SpinnerLoadingIndicatorComponent } from '@cbre/components';
import { environment } from '@env/environment';
import { ReactEmbeddedVersionService } from '@shared/components/react-embed/react-embedded.service';
import { ErrorPageStandaloneComponent } from '@shared/components/error-page/error-page.component-standalone';
import { KadsEmbeddedComponent } from '@shared/components/kads-embed/kads-embedded.component';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { CbreLoggerToken, CbreLogger } from '@cbre/logger';

@Component({
  selector: 'miq-kads',
  templateUrl: './kads.component.html',
  styleUrls: ['./kads.component.scss'],
  standalone: true,
  imports: [CommonModule, SpinnerLoadingIndicatorComponent, KadsEmbeddedComponent, ErrorPageStandaloneComponent],
})
export class KadsComponent implements OnInit {
  $isLoading = signal<boolean>(true);
  $hasError = signal<boolean>(false);
  $scriptsReady = signal<boolean>(false);

  baseUrl = (environment as any).KADS?.FEPrefix ?? null;
  jsPath: string | null = null;
  runtimePath: string | null = null;
  cssPath: string | null = null;
  userId: string | number | null = null;

  /**
   * Initial KADS sub-route derived from the current browser pathname.
   * Populated in ngOnInit so that a direct visit or browser-refresh at
   * /kads/data-management/:id restores the correct view inside KADS.
   */
  derivedInitialRoute = '';

  constructor(
    private userInfo: UserInfoResolver,
    private reactVersionService: ReactEmbeddedVersionService,
    private route: ActivatedRoute,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  ngOnInit(): void {
    // Derive the initial KADS route from Angular's ActivatedRoute snapshot rather
    // than window.location.pathname. Angular populates snapshot.url with the
    // segments that were actually matched for THIS component's child route:
    //   - '' child (menu click to /kads)                → url = []  → ''
    //   - '**' child (refresh at /kads/data-management/:id) → url has segments
    // Using window.location.pathname is unreliable because Angular may update
    // the browser URL after component activation, causing a stale read.
    const segments = this.route.snapshot.url.map(s => s.path).join('/');
    const qp = this.route.snapshot.queryParams as Record<string, string>;
    const search = Object.keys(qp).length > 0 ? '?' + new URLSearchParams(qp).toString() : '';
    this.derivedInitialRoute = segments + search;

    if (!this.baseUrl) {
      this.logger.error('KadsComponent: environment.KADS.FEPrefix is not configured');
      this.$hasError.set(true);
      this.$isLoading.set(false);
      return;
    }
    this.loadConfig();
  }

  onKadsReady(): void {
    this.$isLoading.set(false);
  }

  onErrorWhenEmbedding(): void {
    this.$hasError.set(true);
    this.$isLoading.set(false);
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private getScriptUrls(baseUrl: string) {
    return this.reactVersionService.getVersion(baseUrl).pipe(
      catchError(error => {
        // Redacted: log only safe error metadata (CWE-532)
        this.logger.error(
          `KadsComponent: Error fetching version info message : ${error?.message}, status: ${error?.status}`
        );
        return of(null);
      }),
      map((info: any) => {
        if (!info?.mainjs) {
          this.logger.error('KadsComponent: No mainjs in version info');
          return { mainjs: null, maincss: null, runtime: null };
        }
        const js = info.mainjs.startsWith('/') ? info.mainjs : `/${info.mainjs}`;
        const css = info.maincss ? (info.maincss.startsWith('/') ? info.maincss : `/${info.maincss}`) : null;
        const runtime = info.runtimejs
          ? info.runtimejs.startsWith('/')
            ? info.runtimejs
            : `/${info.runtimejs}`
          : null;

        return { mainjs: js, maincss: css, runtime };
      })
    );
  }

  private loadConfig(): void {
    this.getScriptUrls(this.baseUrl).subscribe(urls => {
      if (!urls.mainjs) {
        this.logger.error('KadsComponent: Missing mainjs in version info');
        this.$hasError.set(true);
        this.$isLoading.set(false);
        return;
      }

      this.jsPath = urls.mainjs;
      this.runtimePath = urls.runtime;
      this.cssPath = urls.maincss;
      this.$scriptsReady.set(true);

      const user = this.userInfo.resolve();
      this.userId = user?.id ?? null;

      // Keep $isLoading true — spinner stays visible until onKadsReady() fires
      // once the <kads-spdm> element fully bootstraps.
      this.$hasError.set(false);
    });
  }
}
